import React, { FC, useEffect, useCallback, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  TextInput,
  Combobox,
  Option,
  Button,
  ButtonSize,
  ButtonColor,
  Loader,
  Modal,
  ModalType,
  Checkbox,
} from '@code/innovance-react-ui-kit';
import { useForm, Controller, FieldError } from 'react-hook-form';
import styled from 'styled-components';
import {
  Container,
  Title,
  Subtitle,
  Form,
  FormRow,
  FormElement,
  Separator,
} from 'src/front/components/FormStyles';
import { useCustomerSearchState } from 'src/front/contexts/LoanApplicationContext';
import useYupResolver from 'src/front/common/hooks/useYupResolver';
import { IndividualInformationSchema } from 'src/front/common/utils/validationSchemas';
import { Customer, LoanIndividualPageValidation } from 'src/front/api/apiInstance';
import { ReferenceDataType } from 'src/front/api/generated';
import usePRM from 'src/front/common/hooks/usePRM';
import { convertStringToDate, formatDate } from 'src/front/common/utils/dateTimeUtils';
import {
  optionFormat,
  dictionaryLineFormat,
  formatAlphaNumericCyrillicPlusDashAndBlank,
  formatFreeText,
  formatAlphaNumericCyrillicSpecialCharacters,
  formatSnils,
  formatAlphaNumericCyrillicPlusDashAndBlankAndDot,
} from 'src/front/common/utils/formUtils';
import Markdown from 'src/front/components//Markdown';
import Datepicker from 'src/front/components//Datepicker';
import { YES_NO } from 'src/front/constant';

const CheckBoxWrapper = styled.div`
  display: flex;
  height: 100%;

  align-items: center;
`;

interface FormData {
  customerSurname: string;
  customerName: string;
  customerSecondName: string;
  customerGender: Option | null;
  birthDate: Date | null;
  birthPlaceCountry: Option | null;
  citizenship: Option | null;
  birthPlaceRegion: Option | null;
  residenceCountry: Option | null;
  birthPlaceCity: string;
  hasPreviousSurname: Option | null;
  previousSurname: string;
  newCustomer: boolean;
  snils: string;
  civilServant: boolean;
  code: string;
}
const defaultValues = {
  customerName: '',
  customerSurname: '',
  customerSecondName: '',
  customerGender: null,
  birthDate: null,
  birthPlaceCountry: null,
  citizenship: null,
  birthPlaceRegion: null,
  residenceCountry: null,
  birthPlaceCity: '',
  hasPreviousSurname: null,
  previousSurname: '',
  snils: '',
  civilServant: false,
  code: '',
};
interface Props {
  goToNextStep: (customerId?: string) => void;
}
const IndividualInformation: FC<Props> = ({ goToNextStep }) => {
  const { t } = useTranslation();
  const {
    isNewCustomer,
    customerNumber,
    customerId,
    customerSearchValues,
  } = useCustomerSearchState();
  const resolver = useYupResolver(IndividualInformationSchema, {
    todayMaxControl: true,
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const countries = usePRM(ReferenceDataType.COUNTRIES);
  const genders = usePRM(ReferenceDataType.GENDER);
  const regions = usePRM(ReferenceDataType.REGION);
  const yesNoComboItems = usePRM(ReferenceDataType.YESNOTYPE);

  const { handleSubmit, errors, register, setValue, watch, control, formState } = useForm<FormData>(
    {
      defaultValues: {
        ...defaultValues,
        newCustomer: isNewCustomer,
        ...(isNewCustomer ? customerSearchValues : {}),
      },
      resolver,
    }
  );
  const loadIndividualInformation = useCallback(async (): Promise<void> => {
    const { data } = await Customer.getCustomerDetailForLoanApplication({
      customerNumber: customerNumber || '',
    });
    const customerGender = genders.find((gender) => gender.value === data?.gender);
    const birthPlaceCountry = countries.find(
      (country) => country.value === data?.birthPlaceCountry
    );
    const citizenship = countries.find((country) => country.value === data?.citizenship);
    const birthPlaceRegion = regions.find((region) => region.value === data?.birthPlaceRegion);
    const residenceCountry = countries.find((country) => country.value === data?.residence);

    [
      { name: 'customerGender', value: customerGender },
      { name: 'birthPlaceCountry', value: birthPlaceCountry },
      { name: 'citizenship', value: citizenship },
      { name: 'residenceCountry', value: residenceCountry },
      { name: 'customerName', value: data.customerName },
      { name: 'customerSurname', value: data.customerSurname },
      { name: 'customerSecondName', value: data.customerSecondName },
      { name: 'birthDate', value: convertStringToDate(data.birthDate) },
      { name: 'birthPlaceCity', value: data.birthPlaceText },
      { name: 'birthPlaceRegion', value: birthPlaceRegion },
      {
        name: 'hasPreviousSurname',
        value: data.hasPreviousSurname ? optionFormat(data.hasPreviousSurname) : null,
      },
      { name: 'previousSurname', value: data.previousSurname },
      { name: 'snils', value: data.snils },
      { name: 'civilServant', value: data.isCivilServant },
      { name: 'code', value: data.codeWordForSupportCentre },
    ].forEach(({ name, value }) => setValue(name, value as {} | undefined));
    // TODO(ozgunbal): revisit to fix type error

    setIsLoading(false);
  }, [countries, customerNumber, genders, regions, setValue]);

  useEffect(() => {
    register({ name: 'birthDate' });
    register({ name: 'birthPlaceRegion' });
    register({ name: 'newCustomer' });
    register({ name: 'civilServant' });
    register({ name: 'citizenship' });
    register({ name: 'residenceCountry' });
  }, [register]);

  const handleDateChange = (date: Date): void =>
    setValue('birthDate', date, {
      shouldValidate: formState.isSubmitted || !!date,
      shouldDirty: formState.isSubmitted || !!date,
    });
  const handleBirthPlaceRegion = (region: Option | null): void =>
    setValue('birthPlaceRegion', region, { shouldValidate: true, shouldDirty: true });

  const handleModalClose = (): void => setIsModalOpen(false);

  const handleModalSubmit = (): void => goToNextStep(customerId);

  const loadFormFields = useCallback(async (): Promise<void> => {
    const { data } = await LoanIndividualPageValidation.getLoanIndividualPageData();

    if (data.result === 'OK' && data.pageData) {
      const {
        customerGender,
        citizenship,
        birthDate,
        birthPlaceCity,
        residenceCountry,
        birthPlaceRegion,
        birthPlaceCountry,
        customerSurname,
        customerName,
        customerSecondName,
        hasPreviousSurname,
        previousSurname,
        isCivilServant,
        snils,
        codeWordForSupportCentre,
      } = data.pageData;
      [
        { name: 'customerGender', value: customerGender ? optionFormat(customerGender) : '' },
        {
          name: 'birthPlaceCountry',
          value: birthPlaceCountry ? optionFormat(birthPlaceCountry) : null,
        },
        { name: 'citizenship', value: optionFormat(citizenship) },
        { name: 'residenceCountry', value: optionFormat(residenceCountry) },
        { name: 'customerName', value: customerName },
        { name: 'customerSurname', value: customerSurname },
        { name: 'customerSecondName', value: customerSecondName },
        { name: 'birthDate', value: convertStringToDate(birthDate) },
        { name: 'birthPlaceCity', value: birthPlaceCity },
        { name: 'birthPlaceRegion', value: optionFormat(birthPlaceRegion) },
        {
          name: 'hasPreviousSurname',
          value: hasPreviousSurname ? optionFormat(hasPreviousSurname) : null,
        },
        { name: 'previousSurname', value: previousSurname },
        { name: 'isCivilServant', value: isCivilServant },
        { name: 'snils', value: snils },
        { name: 'code', value: codeWordForSupportCentre },
      ].forEach(({ name, value }) => setValue(name, value as {} | undefined));
    } else if (data.result === 'OK' && !data.pageData) {
      [
        { name: 'citizenship', value: countries[0] },
        { name: 'residenceCountry', value: countries[0] },
      ].forEach(({ name, value }) => setValue(name, value as {} | undefined));
    }
    setIsLoading(false);
  }, [countries, setValue]);

  const onSubmit = async (form: FormData): Promise<void> => {
    const response = await LoanIndividualPageValidation.validateLoanIndividualPageData({
      customerId,
      customerNumber,
      customerSurname: form.customerSurname,
      customerName: form.customerName,
      customerSecondName: form.customerSecondName,
      customerGender: dictionaryLineFormat(form.customerGender),
      birthDate: formatDate(form.birthDate),
      birthPlaceCountry: dictionaryLineFormat(form.birthPlaceCountry),
      citizenship: dictionaryLineFormat(form.citizenship),
      birthPlaceRegion: dictionaryLineFormat(form.birthPlaceRegion),
      residenceCountry: dictionaryLineFormat(form.residenceCountry),
      birthPlaceCity: form.birthPlaceCity,
      ...(form.hasPreviousSurname
        ? { hasPreviousSurname: dictionaryLineFormat(form.hasPreviousSurname) }
        : { hasPreviousSurname: dictionaryLineFormat(yesNoComboItems[0]) }),
      previousSurname: form.previousSurname,
      isCivilServant: form.civilServant,
      snils: form.snils ? form.snils : undefined,
      codeWordForSupportCentre: form.code,
    });

    if (response.data.result === 'OK' && response.data.warning) {
      setErrorMessage(response.data.warning);
      setIsModalOpen(true);
    } else if (response.data.result === 'OK') {
      goToNextStep(customerId);
    }
  };
  useEffect(() => {
    if (genders.length && countries.length && regions.length) {
      if (customerId) {
        loadIndividualInformation();
      } else {
        loadFormFields();
      }
    }
  }, [
    countries.length,
    customerId,
    genders.length,
    loadFormFields,
    loadIndividualInformation,
    regions.length,
  ]);
  return (
    <Container>
      {isModalOpen ? (
        <Modal
          cancelText={t('general_cancel_button')}
          description={<Markdown text={errorMessage} />}
          onCancel={handleModalClose}
          onSubmit={handleModalSubmit}
          submitText={t('general_components_ok_button')}
          title={t('general_confirmationPopUp_title')}
          type={ModalType.Confirmation}
        />
      ) : null}
      <Title>
        <Trans i18nKey="cardEntry_customerInfo_individual_subtitle" />
      </Title>
      <Subtitle>
        <Trans i18nKey="cardEntry_customerInfo_title" />
      </Subtitle>
      <Form onSubmit={handleSubmit(onSubmit)}>
        {isLoading ? <Loader /> : null}
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="customerSurname"
              render={({ onChange, value }) => (
                <TextInput
                  errorMessage={t(errors?.customerSurname?.message as string)}
                  formatter={formatAlphaNumericCyrillicPlusDashAndBlank}
                  label={t('cardEntry_customerInfo_surname_label')}
                  maxLength={150}
                  name="customerSurname"
                  onChange={onChange}
                  value={value}
                  disabled
                  required
                />
              )}
            />
          </FormElement>
          <FormElement>
            <Controller
              control={control}
              name="customerName"
              render={({ onChange, value }) => (
                <TextInput
                  errorMessage={t(errors?.customerName?.message as string)}
                  formatter={formatAlphaNumericCyrillicPlusDashAndBlank}
                  label={t('cardEntry_customerInfo_name_label')}
                  maxLength={150}
                  name="customerName"
                  onChange={onChange}
                  value={value}
                  disabled
                  required
                />
              )}
            />
          </FormElement>
        </FormRow>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="customerSecondName"
              render={({ onChange, value }) => (
                <TextInput
                  disabled={!isNewCustomer || !!customerSearchValues?.customerSecondName}
                  formatter={formatAlphaNumericCyrillicPlusDashAndBlankAndDot}
                  label={t('cardEntry_customerInfo_middleName_label')}
                  maxLength={150}
                  name="customerSecondName"
                  onChange={onChange}
                  value={value}
                />
              )}
            />
          </FormElement>
          <FormElement>
            <Controller
              control={control}
              name="customerGender"
              render={({ onChange, value }) => (
                <Combobox
                  disabled={!isNewCustomer}
                  errorMessage={t((errors?.customerGender as FieldError)?.message as string)}
                  items={genders}
                  label={t('cardEntry_customerInfo_gender_label')}
                  name="customerGender"
                  onChange={onChange}
                  value={value}
                  required
                />
              )}
            />
          </FormElement>
        </FormRow>
        <Separator />
        <Subtitle>
          <Trans i18nKey="cardEntry_customerInfo_birthAndPlaces_text" />{' '}
        </Subtitle>
        <FormRow>
          <FormElement>
            <Datepicker
              errorMessage={t(errors?.birthDate?.message as string)}
              inputLabel={`${t('cardEntry_customerInfo_birthdate_label')} *`}
              onChange={handleDateChange}
              value={watch('birthDate') as Date}
              disabled
            />
          </FormElement>
          <FormElement>
            <Controller
              control={control}
              name="birthPlaceCountry"
              render={({ onChange, value }) => (
                <Combobox
                  disabled={!isNewCustomer}
                  errorMessage={t((errors?.birthPlaceCountry as FieldError)?.message as string)}
                  items={countries}
                  label={t('cardEntry_customerInfo_birthPlaceCountry_label')}
                  name="birthPlaceCountry"
                  onChange={onChange}
                  searchPlaceholder={t('cardEntry_customerInfo_searchPlaceOfBirth_text')}
                  value={value}
                  required
                  searchable
                />
              )}
            />
          </FormElement>
        </FormRow>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="citizenship"
              render={({ onChange, value }) => (
                <Combobox
                  errorMessage={t((errors?.citizenship as FieldError)?.message as string)}
                  items={countries}
                  label={t('cardEntry_customerInfo_citizenship_label')}
                  name="citizenship"
                  onChange={onChange}
                  searchPlaceholder={t('cardEntry_customerInfo_searchCitizenship_text')}
                  value={value}
                  disabled
                  required
                  searchable
                />
              )}
            />
          </FormElement>
          <FormElement>
            {watch('birthPlaceCountry')?.value === 'RU' ? (
              <Combobox
                disabled={!isNewCustomer}
                errorMessage={t((errors?.birthPlaceRegion as FieldError)?.message as string)}
                items={regions}
                label={t('cardEntry_customerInfo_birthPlaceRegion_label')}
                name="birthPlaceRegion"
                onChange={handleBirthPlaceRegion}
                searchPlaceholder={t('cardEntry_customerInfo_searchPlaceOfBirthRegion_text')}
                value={watch('birthPlaceRegion')}
                required
                searchable
              />
            ) : null}
          </FormElement>
        </FormRow>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="residenceCountry"
              render={({ onChange, value }) => (
                <Combobox
                  errorMessage={t((errors?.residenceCountry as FieldError)?.message as string)}
                  items={countries}
                  label={t('cardEntry_customerInfo_residenceCountry_label')}
                  name="residenceCountry"
                  onChange={onChange}
                  searchPlaceholder={t('cardEntry_customerInfo_searchPlaceOfBirth_text')}
                  value={value}
                  disabled
                  required
                  searchable
                />
              )}
            />
          </FormElement>
          <FormElement>
            <Controller
              control={control}
              name="birthPlaceCity"
              render={({ onChange, value }) => (
                <TextInput
                  disabled={!isNewCustomer}
                  errorMessage={t(errors?.birthPlaceCity?.message as string)}
                  formatter={formatFreeText}
                  label={t('cardEntry_customerInfo_birthPlace_label')}
                  maxLength={150}
                  name="birthPlaceCity"
                  onChange={onChange}
                  value={value}
                  required
                />
              )}
            />
          </FormElement>
        </FormRow>

        <Subtitle>
          <Trans i18nKey="cardEntry_customerInfo_previousSurname_label" />
        </Subtitle>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="hasPreviousSurname"
              render={({ onChange, value }) => (
                <Combobox
                  errorMessage={t((errors?.hasPreviousSurname as FieldError)?.message as string)}
                  items={yesNoComboItems}
                  label={t('cardEntry_customerInfo_surnameChange_label')}
                  name="hasPreviousSurname"
                  onChange={onChange}
                  value={value}
                  required
                />
              )}
            />
          </FormElement>
          {watch('hasPreviousSurname')?.value === YES_NO.YES ? (
            <FormElement>
              <Controller
                control={control}
                name="previousSurname"
                render={({ onChange, value }) => (
                  <TextInput
                    errorMessage={t(errors?.previousSurname?.message as string)}
                    formatter={formatAlphaNumericCyrillicSpecialCharacters}
                    label={t('cardEntry_customerInfo_previousSurname_label')}
                    maxLength={150}
                    name="previousSurname"
                    onChange={onChange}
                    value={value}
                    required
                  />
                )}
              />
            </FormElement>
          ) : null}
        </FormRow>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="code"
              render={({ onChange, value }) => (
                <TextInput
                  errorMessage={t(errors?.previousSurname?.message as string)}
                  formatter={formatAlphaNumericCyrillicSpecialCharacters}
                  label={t('loanApp_customerInfo_mothermaidenname_label')}
                  maxLength={150}
                  name="code"
                  onChange={onChange}
                  value={value}
                />
              )}
            />
          </FormElement>
        </FormRow>
        <Subtitle>
          <Trans i18nKey="loanApp_customerInfo_other_label" />
        </Subtitle>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="snils"
              render={({ onChange, value }) => (
                <TextInput
                  errorMessage={t(errors?.snils?.message as string)}
                  formatter={formatSnils}
                  label={t('loanApp_customerInfo_snils_label')}
                  maxLength={14}
                  name="snils"
                  onChange={onChange}
                  value={value}
                />
              )}
            />
          </FormElement>
          <FormElement>
            <CheckBoxWrapper>
              <Checkbox
                id="civilServant"
                label={t('loanApp_customerInfo_civilServant_label')}
                onChange={({ target }) => setValue('civilServant', target.checked)}
                value={watch('civilServant')}
              />
            </CheckBoxWrapper>
          </FormElement>
        </FormRow>
        <FormRow>
          <Button
            color={ButtonColor.Primary}
            disabled={formState.isSubmitting}
            size={ButtonSize.Medium}
            text={t('general_continue_button')}
            type="submit"
          />
        </FormRow>
      </Form>
    </Container>
  );
};

export default IndividualInformation;
